'use client';

import { Navigation, UsageConsentModal } from '@/components';
import { useActiveCertificationFromRoute } from '@/hooks';
import { useGlobalStore } from '@/store';
import { useStoreInitialization } from '@/store/hooks/useStoreInitialization';
import { usePathname } from 'next/navigation';
import React, { useMemo } from 'react';

/**
 * Template
 *
 * The root template for the application that wraps all pages. It handles
 * global concerns such as navigation controls and the usage consent modal.
 */
export default function Template({ children }: { children: React.ReactNode }) {
  const { usageConsentAccepted } = useStoreInitialization();
  const { currentUser, usageConsentAccepted: usageConsentAcceptedFromStore } = useGlobalStore();
  const pathname = usePathname();
  const shouldHideUsageConsentModal = pathname.includes('/auth/');
  useActiveCertificationFromRoute();

  /**
   * Determine whether to show navigation controls
   */
  const showNavigationControls = useMemo(() => {
    // Split the pathname by '/' and get the last non-empty segment
    const segments = pathname.split('/').filter((segment) => segment.length > 0);
    const lastPathSegment = segments[segments.length - 1];

    if (lastPathSegment === 'roleplay') return false;
    else if (!usageConsentAccepted) return false;
    else if (usageConsentAcceptedFromStore) return true;
    else return true;
  }, [usageConsentAccepted, usageConsentAcceptedFromStore, pathname]);

  // Render
  return (
    <Navigation showControls={showNavigationControls}>
      {currentUser != null && !usageConsentAccepted && !shouldHideUsageConsentModal ? (
        <UsageConsentModal />
      ) : (
        children
      )}
    </Navigation>
  );
}
